import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBranchComparison, getBranches } from "../api";

const NAVY = "#0f172a";
const PRIMARY = "#1e3a8a";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ExpenseBranchComparison() {
  const [month] = useState(currentMonth());
  const { data: branchesResp } = useQuery({ queryKey: ["branches"], queryFn: getBranches });
  const { data, isLoading } = useQuery({ queryKey: ["branch-comparison", month], queryFn: () => getBranchComparison(month) });

  const employeeCounts: Record<number, number> = {};
  (branchesResp?.branches ?? []).forEach(b => { employeeCounts[b.id] = b.employee_count; });

  const branches = data?.branches ?? [];
  // Each branch can carry more than one currency (C5-13) — the bar width compares
  // against the largest single-currency amount across all branches, an approximation
  // since there's no cross-currency conversion to compare against directly.
  const maxAmount = Math.max(1, ...branches.flatMap(b => b.amounts.map(a => a.amount)));

  if (isLoading) return <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading…</div>;

  return (
    <div>
      <h2 style={{ margin: "0 0 4px", color: NAVY, fontSize: 18 }}>Branches — {month}</h2>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
        Confirmed expense spend per location (expense-side only — no sales data).
      </p>
      <div style={{ display: "grid", gap: 10 }}>
        {branches.map(b => (
          <div key={b.branch_id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>{b.branch_name}</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: "#94a3b8" }}>
                  {employeeCounts[b.branch_id] ?? 0} employee{(employeeCounts[b.branch_id] ?? 0) !== 1 ? "s" : ""}
                </span>
              </div>
              <div style={{ textAlign: "right" }}>
                {b.amounts.map(a => (
                  <span key={a.currency} style={{ marginLeft: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: NAVY }}>
                      {a.currency_symbol}{a.amount.toLocaleString("en", { minimumFractionDigits: 0 })}
                    </span>
                    {a.pct_change_vs_last_month != null && (
                      <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: a.pct_change_vs_last_month >= 0 ? "#dc2626" : "#16a34a" }}>
                        {a.pct_change_vs_last_month >= 0 ? "▲" : "▼"} {Math.abs(a.pct_change_vs_last_month)}%
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ height: 6, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(Math.max(0, ...b.amounts.map(a => a.amount)) / maxAmount) * 100}%`, background: PRIMARY, borderRadius: 4 }} />
            </div>
          </div>
        ))}
        {branches.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>No confirmed expenses yet this month.</div>
        )}
      </div>
    </div>
  );
}
